import { ApolloError } from '@apollo/client';
import {
  authenticationChannel,
  authenticationContext,
  sessionManagement,
  ActivityMonitor,
  commonActions
} from '@codametrix/ui-common';
import { showModalMessage } from '../actions/feedback.actions';

const { activityMonitor } = sessionManagement;

const isAuthError = (e: unknown): boolean => {
  if (e instanceof ApolloError) {
    if ((e.networkError as any)?.statusCode === 401) return true;
    if (e.graphQLErrors.some(err => err.extensions?.code === 'UNAUTHENTICATED')) return true;
  }
  if (e instanceof Error && e.message.includes('No authentication token')) return true;
  return false;
};

const dispatchLogoutModal = () => {
  // Lazy require to avoid circular dependency:
  // service-desk.client → store → reducers → actions → service-desk.client
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const store = require('../store/store').default;
  store.dispatch(
    showModalMessage({
      title: 'You have been logged out.',
      buttonText: 'Log in',
      closable: false,
      handler: commonActions.UserInterface.LOGOUT,
      forceSubmitOptions: { isForced: true, message: 'Navigating to login page in ', time: 10 }
    }) as any
  );
};

let _client: any = null;
let _didRefreshListener: (() => void) | null = null;
let _logoutResetRegistered = false;

/** Exposed only for unit tests — resets the singleton so each test gets a fresh client. */
export const _resetClientForTesting = () => {
  _client = null;
  _didRefreshListener = null;
};

// Logout revokes the bearer token server-side and clears sessionStorage, but it does
// not reload the page — so without this the cached widget, and the bearerToken it
// captured once in its constructor, survive into the next session. ServiceDesk.create()
// only re-acquires a token when isTokenValid() is false, and that check compares the
// JWT `exp` alone, so it cannot see revocation: the dead token is treated as good,
// reused for every request, and written back over the fresh token in sessionStorage by
// renderWidget(). Everything then 401s until a full page reload — which logout, unlike
// a context switch, never performs. Dropping the client on cmx-logout forces the next
// getServiceDeskClient() to build a widget that re-acquires the current token.
const registerLogoutReset = () => {
  if (_logoutResetRegistered) return;
  globalThis.addEventListener('cmx-logout', () => {
    if (_didRefreshListener) {
      activityMonitor.off(ActivityMonitor.events.DID_REFRESH, _didRefreshListener);
      _didRefreshListener = null;
    }
    _client = null;
  });
  _logoutResetRegistered = true;
};

// Wraps the two Apollo methods that can surface auth errors so every caller
// (manage-coders, cases, queue-priority-order, and any future action) gets
// the logout modal dispatched automatically on auth failure. Called via
// ServiceDesk's onApolloClientReady hook every time a client instance is
// created — both the initial one (assigned asynchronously after
// getBearerToken() resolves) and every replacement produced by
// refreshToken() — so the wrap can never be skipped or lost on reassignment.
const wrapApolloClient = (apolloClient: any) => {
  const origQuery = apolloClient.query.bind(apolloClient);
  apolloClient.query = async (...args: any[]) => {
    try { return await origQuery(...args); }
    catch (e) { if (isAuthError(e)) dispatchLogoutModal(); throw e; }
  };

  const origMutate = apolloClient.mutate.bind(apolloClient);
  apolloClient.mutate = async (...args: any[]) => {
    try { return await origMutate(...args); }
    catch (e) { if (isAuthError(e)) dispatchLogoutModal(); throw e; }
  };
};

export const getServiceDeskClient = () => {
  registerLogoutReset();
  if (_client) return _client;

  //@ts-ignore
  const xcaliberClient = ServiceDesk.init({
    theme: {},
    bffURL: '/api/service-desk/bff',
    getBearerToken: async () => {
      await authenticationChannel.ready;
      const token = authenticationContext.getToken();
      if (!token) {
        // Honest contract: signal "no credential" rather than resolving a fake
        // empty token (which would leave consumers issuing 401 requests). Callers
        // gate on an active session (see hasActiveSession in cases.action.ts) so
        // this is not reached during expected logout teardown.
        throw new Error('No authentication token available for Service Desk');
      }
      //Check if token already has text Bearer, appened only if it not present
      if (token && token.indexOf('Bearer') !== -1) {
        return `${token}`;
      }
      return `Bearer ${token}`;
    },
    onApolloClientReady: wrapApolloClient
  });

  // Held in module scope so the logout reset can detach it; otherwise every
  // logout/login cycle would leave a listener refreshing a discarded client.
  _didRefreshListener = async () => {
    xcaliberClient.refreshToken();
  };
  activityMonitor.on(ActivityMonitor.events.DID_REFRESH, _didRefreshListener);

  const origRefreshToken = xcaliberClient.refreshToken.bind(xcaliberClient);
  xcaliberClient.refreshToken = async (...args: any[]) => {
    try { return await origRefreshToken(...args); }
    catch (e) { if (isAuthError(e)) dispatchLogoutModal(); throw e; }
  };

  _client = xcaliberClient;
  return _client;
};
